import { __awaiter } from '../../../../../_virtual/_tslib.js';
import { useCallback } from 'react';
import { object, string } from 'yup';
import { useInternalDynamicContext } from '../../../context/DynamicContext/useInternalDynamicContext.js';
import { updateUserProfileFields } from '../../../data/api.js';
import { isEmailVerificationRequired } from '../../functions/authenication/isEmailVerificationRequired/isEmailVerificationRequired.js';
import { useEmailVerificationContext } from '../../../context/EmailVerificationContext/EmailVerificationContext.js';
import { useEmailVerificationRequest } from '../authenticationHooks/useEmailVerificationRequest/useEmailVerificationRequest.js';
import { useUpdateUserProfileByJWTCallback } from '../useUpdateUserProfileByJWTCallback/useUpdateUserProfileByJWTCallback.js';

// User Fields Schema without captchaToken and policiesConsent properties
const userFieldsSchema = object().shape({
    email: string(),
    alias: string(),
    firstName: string(),
    lastName: string(),
    jobTitle: string(),
    phoneNumber: string(),
    tShirtSize: string(),
    team: string(),
    country: string(),
});
// Hook exposed to the public
const useUserUpdateRequestInternal = ({ validationSchemaStripUnknown, }) => {
    const { authToken, onboardingOnlyJwt, environmentId } = useInternalDynamicContext();
    const jwtToken = onboardingOnlyJwt || authToken;
    const { setVerificationUUID, setEmail } = useEmailVerificationContext();
    const { verifyEmail: verifyEmailFn } = useEmailVerificationRequest();
    const { updateUserProfileByJWT } = useUpdateUserProfileByJWTCallback();
    const updateUser = useCallback((userFields, userEnvironmentId) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        const validatedUserFields = yield userFieldsSchema.validate(userFields, {
            stripUnknown: validationSchemaStripUnknown,
        });
        const updateUserProfileResponse = yield updateUserProfileFields(jwtToken, userEnvironmentId || environmentId, validatedUserFields);
        const _isEmailVerificationRequired = isEmailVerificationRequired(updateUserProfileResponse);
        if (!_isEmailVerificationRequired) {
            // If email verification is not needed then we can update the user profile immediately.
            updateUserProfileByJWT(updateUserProfileResponse.jwt);
            return {
                isEmailVerificationRequired: _isEmailVerificationRequired,
                updateUserProfileResponse,
            };
        }
        // We update EmailContext to use this information in the Email Verification view
        setEmail((_a = updateUserProfileResponse.emailVerification) === null || _a === void 0 ? void 0 : _a.email);
        setVerificationUUID((_b = updateUserProfileResponse.emailVerification) === null || _b === void 0 ? void 0 : _b.verificationUUID);
        // DYN-2046 - Customers have the opportunity to programmatically use our email verification and profile editing.
        // VerifyEmailFN was scoped to bypass the problem with memoization of the state at the beginning of Promise.
        // setEmail and setVerificationUUID in the above lines will are not visible in this promise.
        const scopedVerifyEmailFn = (verificationToken) => {
            var _a;
            return verifyEmailFn(verificationToken, (_a = updateUserProfileResponse.emailVerification) === null || _a === void 0 ? void 0 : _a.verificationUUID);
        };
        return {
            isEmailVerificationRequired: _isEmailVerificationRequired,
            updateUserProfileResponse,
            verifyEmailFn: scopedVerifyEmailFn,
        };
    }), [
        environmentId,
        jwtToken,
        setEmail,
        setVerificationUUID,
        validationSchemaStripUnknown,
        updateUserProfileByJWT,
        verifyEmailFn,
    ]);
    return { updateUser };
};
// Hook exposed to the clients
// We do not want customers to be able to edit properties such as policiesConsent or captchaToken
const useUserUpdateRequest = () => 
// eslint-disable-next-line react-hooks/rules-of-hooks
useUserUpdateRequestInternal({ validationSchemaStripUnknown: true });

export { useUserUpdateRequest, useUserUpdateRequestInternal };
